import { defined } from "@glideapps/ts-necessities";
import { logError, logInfo } from "common/debug-print";
import { getFeatureFlag } from "common/feature-flags";
import { isUndefinedish } from "common/Support";

interface AnxiousIDBOpenDBRequest extends IDBOpenDBRequest {
    _setError(e: DOMException): void;
    _setForwardee(f: IDBOpenDBRequest): void;
}

const baseTimeout = 500;
const timeoutScale = 1.4;
const retries = 7;

function anxiousIDBOpenWithResponder<T extends (...argv: any[]) => IDBOpenDBRequest>(
    timeout: number,
    remainingRetries: number,
    respondTo: AnxiousIDBOpenDBRequest,
    factory: IDBFactory,
    opener: T,
    args: any[]
) {
    let gaveUp: boolean = false;
    const internal = opener.apply(factory, args);
    respondTo._setForwardee(internal);

    const retryTimeout = setTimeout(() => {
        // We're actually done, not pending. Here's hoping Safari isn't so buggy
        // that it doesn't fire events when the readyState is "done".
        // Note that we check whether we've given up so that injected faults
        // won't cause a deadlock; fault injection also sets gaveUp to true.
        if (!gaveUp && internal.readyState === "done") return;

        gaveUp = true;
        if (remainingRetries > 0) {
            anxiousIDBOpenWithResponder(timeout * timeoutScale, remainingRetries - 1, respondTo, factory, opener, args);
        } else {
            const error = new DOMException("Database connection failed", "UnknownError");
            respondTo._setError(error);
            respondTo.dispatchEvent(new Event("error"));
        }
    }, timeout);

    const handleEvent = (ev: Event) => {
        if (gaveUp || (getFeatureFlag("injectLocalDatastorePersistenceFaults") && remainingRetries > 4)) {
            gaveUp = true;
            if (!isUndefinedish(internal.result)) {
                logError("Closing valid IndexedDB connection because we gave up", remainingRetries);
                internal.result.close();
            }
            return;
        }
        clearTimeout(retryTimeout);
        if (ev.type === "success") {
            logInfo("Opened IndexedDB", internal.result.name);
            internal.result.addEventListener("close", () => {
                logInfo("Closed IndexedDB", internal.result.name);
            });
        }
        const cloned =
            ev instanceof IDBVersionChangeEvent
                ? new IDBVersionChangeEvent(ev.type, { oldVersion: ev.oldVersion, newVersion: ev.newVersion })
                : new Event(ev.type);
        respondTo.dispatchEvent(cloned);
    };

    internal.onblocked = handleEvent;
    internal.onerror = handleEvent;
    internal.onsuccess = handleEvent;
    internal.onupgradeneeded = handleEvent;
}

// It's not possible to extend IDBOpenDBRequest; if you do so you incur
// several runtime type errors. However, it's possible to extend EventTarget.
// The only downside here is that IDBOpenDBRequest is not part of the inheritance
// chain, but who's checking anyway?
class InternalIDBOpenDBRequest extends EventTarget implements AnxiousIDBOpenDBRequest {
    public onblocked: ((this: IDBOpenDBRequest, ev: Event) => any) | null = null;
    public onupgradeneeded: ((this: IDBOpenDBRequest, ev: IDBVersionChangeEvent) => any) | null = null;
    public onerror: ((this: IDBRequest<IDBDatabase>, ev: Event) => any) | null = null;
    public onsuccess: ((this: IDBRequest<IDBDatabase>, ev: Event) => any) | null = null;

    constructor() {
        super();
        this.addEventListener("blocked", ev => this.onblocked?.(ev));
        this.addEventListener("upgradeneeded", ev => this.onupgradeneeded?.(ev as any));
        this.addEventListener("error", ev => this.onerror?.(ev));
        this.addEventListener("success", ev => this.onsuccess?.(ev));
    }

    private _explicitError: DOMException | undefined;
    private _forwardee: IDBOpenDBRequest | undefined;

    public get error(): DOMException | null {
        if (this._explicitError !== undefined) return this._explicitError;
        return defined(this._forwardee).error;
    }

    public get readyState(): "pending" | "done" {
        if (this._explicitError !== undefined) return "done";
        return defined(this._forwardee).readyState;
    }

    public get result(): any {
        if (this._explicitError !== undefined) return undefined;
        return defined(this._forwardee).result;
    }

    public get source(): any {
        if (this._explicitError !== undefined) return null;
        return defined(this._forwardee).source;
    }

    public get transaction(): any {
        if (this._explicitError !== undefined) return null;
        return defined(this._forwardee).transaction;
    }

    public _setError(e: DOMException) {
        this._explicitError = e;
    }

    public _setForwardee(f: IDBOpenDBRequest) {
        this._forwardee = f;
    }
}

function anxiousIDBOpenGeneric<T extends (...argv: any[]) => IDBOpenDBRequest>(
    factory: IDBFactory,
    opener: T,
    args: any[]
): IDBOpenDBRequest {
    const response = new InternalIDBOpenDBRequest();
    anxiousIDBOpenWithResponder(baseTimeout, retries, response, factory, opener, args);

    // idb uses instanceof checks to do nice things, but we would break those checks
    // if we didn't intentionally establish a proxy that lies about its inheritance chain.
    //
    // Yes this is much nastier as a result. I wish it didn't have to be.
    // - Dan

    const boundAddEventListener = (
        type: string,
        listener: EventListenerOrEventListenerObject | null,
        options?: boolean | AddEventListenerOptions
    ) => {
        return response.addEventListener(type, listener, options);
    };

    const boundDispatchEvent = (ev: Event) => {
        return response.dispatchEvent(ev);
    };

    const boundRemoveEventListener = (
        type: string,
        callback: EventListenerOrEventListenerObject | null,
        options?: EventListenerOptions | boolean
    ) => {
        return response.removeEventListener(type, callback, options);
    };

    const proxy = new Proxy(response as any, {
        get: (_, prop) => {
            // We have to explicitly trap the event listener methods to ensure
            // the call flows down into the actual response. Since those are all
            // native functions, the usual swizzling attempts don't work out.
            switch (prop.toString()) {
                case "addEventListener":
                    return boundAddEventListener;
                case "dispatchEvent":
                    return boundDispatchEvent;
                case "removeEventListener":
                    return boundRemoveEventListener;
            }
            const resp = (response as any)[prop];
            return resp;
        },
        getPrototypeOf: () => {
            // To make idb happy: we're an IDBOpenDBRequest.
            return IDBOpenDBRequest.prototype;
        },
    });

    return proxy;
}

interface IndexedDBFactoryContainer {
    indexedDB: IDBFactory | undefined;
}

const boundTargets: WeakSet<IndexedDBFactoryContainer> = new WeakSet();

// See https://bugs.webkit.org/show_bug.cgi?id=226547 for the motivation behind
// this. It is possible on certain versions of Safari for the initial connection
// request to hang indefinitely. However, a valid workaround is to make multiple
// connection requests: the later ones succeed where the eariler ones fail.
export function hookIndexedDBForUnreliableOpens(target: IndexedDBFactoryContainer) {
    if (target.indexedDB === undefined) return;
    if (boundTargets.has(target)) return;

    const origOpen = target.indexedDB.open;
    target.indexedDB.open = (name: string, version: number | undefined) =>
        anxiousIDBOpenGeneric(defined(target.indexedDB), origOpen, [name, version]);
    boundTargets.add(target);
}
